import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography, Select, Button, Table, Alert, Space, Tooltip, InputNumber } from "antd";
import type { TableColumnType } from "antd";
import type { SorterResult } from "antd/es/table/interface";
import { SearchOutlined, ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { fetchTbfRanking, type TbfDirection, type TbfRankingRequest, type TbfRankingRow } from "../../utils/tbfApi";
import { fetchOicCountries, getFlagUrl } from "../../utils/countriesApi";
import TbfExportService from "../../components/exportServices/TbfExportService";
import ExportButton from "../../components/ExportButton";
import TbfCountryList, { normalize, type TbfCountry } from "./TbfCountryList";
import "./TbfHome.scss";

const { Title, Paragraph, Text } = Typography;

// Matches MAX_POSITION on the backend.
const MAX_POSITION = 20;
const POSITIONS = Array.from({ length: MAX_POSITION }, (_, i) => i + 1);
const OIC_MEMBER_COUNT = 57;
const DIRECTIONS: { value: TbfDirection; icon: React.ReactNode }[] = [
  { value: "top", icon: <ArrowUpOutlined /> },
  { value: "bottom", icon: <ArrowDownOutlined /> },
];

interface TbfResult {
  cCodes: number[];
  direction: TbfDirection;
  position: number;
  firstYear: number;
  lastYear: number;
  minCountries: number;
  rows: TbfRankingRow[];
}

type TbfColumn = Omit<TableColumnType<TbfRankingRow>, "sorter"> & {
  key: string;
  sorter: (a: TbfRankingRow, b: TbfRankingRow) => number;
};

export default function TbfHome({ admin = false }: { admin?: boolean }) {
  const { t, i18n } = useTranslation();

  const [countries, setCountries] = useState<TbfCountry[]>([]);
  const [countriesError, setCountriesError] = useState(false);

  const [cCodes, setCCodes] = useState<number[]>([]);
  const [direction, setDirection] = useState<TbfDirection>("top");
  const [position, setPosition] = useState(1);
  // Hides indicators ranked among fewer countries than this.
  const [minRanked, setMinRanked] = useState(1);

  // Last "Display" request; refetched when the language changes.
  const [request, setRequest] = useState<TbfRankingRequest | null>(null);
  const [result, setResult] = useState<TbfResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [sort, setSort] = useState<SorterResult<TbfRankingRow> | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCountriesError(false);
    fetchOicCountries(i18n.language)
      .then((list) => {
        if (!cancelled) setCountries(list);
      })
      .catch(() => {
        if (!cancelled) setCountriesError(true);
      });
    return () => { cancelled = true; };
  }, [i18n.language]);

  const countryByCode = useMemo(
    () => Object.fromEntries(countries.map((c) => [c.c_code, c])),
    [countries]
  );

  const countryOptions = useMemo(
    () =>
      [...countries]
        .sort((a, b) => a.name.localeCompare(b.name, i18n.language))
        .map((c) => ({
          value: c.c_code,
          name: c.name,
          label: (
            <span className="tbf-home__country-option">
              <img
                src={getFlagUrl(c.c_code)}
                alt=""
                className="tbf-home__flag"
                onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
              />
              {c.name}
            </span>
          ),
        })),
    [countries, i18n.language]
  );

  // `cancelled` keeps a stale response from overwriting a newer one.
  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(false);
    fetchTbfRanking(request, i18n.language)
      .then((data) => {
        if (cancelled) return;
        setResult({
          cCodes: request.cCodes,
          direction: data?.direction ?? request.direction,
          position: data?.position ?? request.position,
          firstYear: data?.first_year,
          lastYear: data?.last_year,
          minCountries: data?.min_countries,
          rows: data?.results ?? [],
        });
      })
      .catch(() => {
        if (cancelled) return;
        setResult(null);
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [request, i18n.language]);

  // Results stay on screen only while they match the current selections.
  useEffect(() => {
    setRequest(null);
    setResult(null);
    setLoading(false);
    setLoadError(false);
  }, [cCodes, direction, position]);

  const handleDisplay = () => setRequest({ cCodes, direction, position });

  const formatValue = (value: number | null | undefined) =>
    value === null || value === undefined
      ? "—"
      : Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

  // Tied countries take the place matching the search: best for top, worst for bottom.
  const placeOf = (row: TbfRankingRow) => (result?.direction === "bottom" ? row.rank_to : row.rank);

  // Missing values sort last.
  const missingLast = (x: unknown, y: unknown, compare: (x: unknown, y: unknown) => number) => {
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return compare(x, y);
  };
  const byText = (get: (row: TbfRankingRow) => unknown) => (a: TbfRankingRow, b: TbfRankingRow) =>
    missingLast(get(a), get(b), (x, y) =>
      String(x).localeCompare(String(y), i18n.language, { sensitivity: "base" }));
  const byNumber = (get: (row: TbfRankingRow) => unknown) => (a: TbfRankingRow, b: TbfRankingRow) =>
    missingLast(get(a), get(b), (x, y) => Number(x) - Number(y));

  // Filtered here so changing the minimum needs no new request.
  const shownResults = useMemo(
    () => (result ? result.rows.filter((r) => r.ranked_countries >= minRanked) : null),
    [result, minRanked]
  );
  const hiddenCount = result && shownResults ? result.rows.length - shownResults.length : 0;

  // An indicator can appear once per country.
  const indicatorCount = useMemo(
    () => (shownResults ? new Set(shownResults.map((r) => r.ind_code)).size : 0),
    [shownResults]
  );

  // Result count per requested country, most first.
  const perCountry = useMemo(() => {
    if (!shownResults || !result) return [];
    const counts: Record<number, number> = {};
    shownResults.forEach((r) => { counts[r.c_code] = (counts[r.c_code] ?? 0) + 1; });
    return result.cCodes
      .map((cCode) => ({
        cCode,
        name: countryByCode[cCode]?.name ?? String(cCode),
        count: counts[cCode] ?? 0,
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, i18n.language));
  }, [shownResults, result, countryByCode, i18n.language]);

  const handleExport = () => {
    if (!result || !shownResults) return;
    TbfExportService(shownResults, {
      countryByCode,
      direction: result.direction,
      position: result.position,
      placeOf,
      t,
      language: i18n.language,
    }).exportToExcel();
  };

  const showCountryColumn = shownResults !== null && new Set(shownResults.map((r) => r.c_code)).size > 1;

  const optionalColumns: (TbfColumn | false)[] = [
    showCountryColumn && {
      title: t("tbf.columns.country"),
      dataIndex: "c_code",
      key: "country",
      width: 180,
      sorter: byText((row) => countryByCode[row.c_code]?.name),
      render: (cCode) => (
        <span className="tbf-home__result-country">
          <img
            src={getFlagUrl(cCode)}
            alt=""
            className="tbf-home__flag"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />
          {countryByCode[cCode]?.name ?? cCode}
        </span>
      ),
    },
    {
      title: t("tbf.columns.indicator"),
      dataIndex: "indicator",
      key: "indicator",
      sorter: byText((row) => row.indicator),
    },
    {
      title: t("tbf.columns.category"),
      dataIndex: "category",
      key: "category",
      width: 200,
      sorter: byText((row) => row.category),
    },
    {
      title: t("tbf.columns.value"),
      dataIndex: "value",
      key: "value",
      width: 130,
      align: "right",
      className: "tbf-home__nowrap",
      sorter: byNumber((row) => row.value),
      render: formatValue,
    },
    {
      title: t("tbf.columns.unit"),
      dataIndex: "unit",
      key: "unit",
      width: 150,
      sorter: byText((row) => row.unit),
    },
    {
      title: t("tbf.columns.year"),
      dataIndex: "year",
      key: "year",
      width: 90,
      align: "center",
      sorter: byNumber((row) => row.year),
    },
    {
      title: t("tbf.columns.rank"),
      key: "rank",
      width: 170,
      align: "center",
      className: "tbf-home__nowrap",
      // Ties are broken by group size.
      sorter: (a, b) => placeOf(a) - placeOf(b) || a.ranked_countries - b.ranked_countries,
      render: (_, row) =>
        t("tbf.rankOf", { count: placeOf(row), total: row.ranked_countries, ordinal: true }),
    },
  ];
  const columns = optionalColumns.filter((c) => c !== false);

  const sortColumn = sort?.order ? columns.find((c) => c.key === sort.columnKey) : undefined;
  const orderedResults = shownResults && sortColumn
    ? [...shownResults].sort((a, b) => (sort?.order === "descend" ? -1 : 1) * sortColumn.sorter(a, b))
    : shownResults;

  const shadedRows = new Set<TbfRankingRow>();
  let shaded = false;
  orderedResults?.forEach((row, i) => {
    if (i > 0 && row.category !== orderedResults[i - 1].category) shaded = !shaded;
    if (shaded) shadedRows.add(row);
  });

  return (
    <div className="tbf-home">
      <Card className="tbf-home__intro">
        <Title level={3} className="tbf-home__title">{t("tbf.title")}</Title>
        <Paragraph className="tbf-home__intro-text">{t("tbf.intro")}</Paragraph>
      </Card>

      {countriesError ? (
        <Alert type="error" showIcon message={t("tbf.countriesError")} />
      ) : (
        admin && <TbfCountryList countries={countries} value={cCodes} onChange={setCCodes} multiple />
      )}

      <Card className="tbf-home__form">
        {!admin && (
          <div className="tbf-home__step">
            <Text strong className="tbf-home__step-title">{t("tbf.step1Single")}</Text>
            <Select
              className="tbf-home__country-select"
              popupClassName="tbf-home__country-popup"
              virtual={false}
              // Tall enough for all 57 countries in one column, so the list grows down instead of scrolling.
              listHeight={2400}
              placement="bottomLeft"
              dropdownAlign={{ overflow: { adjustY: false } }}
              showSearch
              allowClear
              placeholder={t("tbf.selectCountry")}
              loading={countries.length === 0}
              value={cCodes[0]}
              onChange={(cCode) => setCCodes(cCode == null ? [] : [cCode])}
              filterOption={(input, option) =>
                normalize(option?.name ?? "", i18n.language).includes(normalize(input, i18n.language))}
              options={countryOptions}
            />
          </div>
        )}
        <div className="tbf-home__step">
          <Text strong className="tbf-home__step-title">{t("tbf.step2")}</Text>
          <Space wrap size="middle">
            <div className="tbf-home__direction" role="radiogroup" aria-label={t("tbf.step2")}>
              {DIRECTIONS.map(({ value, icon }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={direction === value}
                  className={["tbf-home__direction-card", direction === value && "is-selected"].filter(Boolean).join(" ")}
                  onClick={() => setDirection(value)}
                >
                  <span>{t(`tbf.${value}`)}</span>
                  {icon}
                </button>
              ))}
            </div>
            <Select
              className="tbf-home__position"
              value={position}
              onChange={setPosition}
              options={POSITIONS.map((p) => ({ value: p, label: p }))}
            />
            {admin && (
              <Space size="small" className="tbf-home__min-ranked">
                <Text>{t("tbf.minRanked")}</Text>
                <InputNumber
                  min={1}
                  max={countries.length || OIC_MEMBER_COUNT}
                  precision={0}
                  value={minRanked}
                  onChange={(v) => setMinRanked(v ?? 1)}
                  aria-label={t("tbf.minRanked")}
                />
              </Space>
            )}
            <Button
              type="primary"
              icon={<SearchOutlined />}
              disabled={cCodes.length === 0}
              loading={loading}
              onClick={handleDisplay}
            >
              {t("tbf.display")}
            </Button>
          </Space>
        </div>
      </Card>

      {loadError && (
        <Alert type="error" showIcon className="tbf-home__error" message={t("tbf.loadError")} />
      )}

      {result !== null && shownResults !== null && (
        <Card
          className="tbf-home__results"
          title={
            <span className="tbf-home__results-summary">
              <span>{t("tbf.results", { count: shownResults.length })}</span>
              <span>{t("tbf.indicatorsCount", { count: indicatorCount })}</span>
              <span>{t("tbf.countries", { count: result.cCodes.length })}</span>
              {hiddenCount > 0 && (
                <span className="tbf-home__hidden-note">
                  {t("tbf.hiddenByMinimum", { count: hiddenCount, min: minRanked })}
                </span>
              )}
            </span>
          }
          extra={
            <div className="tbf-home__results-extra">
              {perCountry.length > 1 && (
                <div className="tbf-home__country-chips">
                  {perCountry.map(({ cCode, name, count }) => {
                    const label = `${name} · ${t("tbf.results", { count })}`;
                    return (
                      <Tooltip key={cCode} title={label}>
                        <span
                          className={`tbf-home__country-chip${count === 0 ? " is-empty" : ""}`}
                          role="img"
                          aria-label={label}
                        >
                          <img
                            src={getFlagUrl(cCode)}
                            alt=""
                            className="tbf-home__flag"
                            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                          />
                          <span className="tbf-home__country-chip-count">{count}</span>
                        </span>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
              {admin && (
                <ExportButton
                  disabled={shownResults.length === 0}
                  onExport={handleExport}
                  errorMessage={t("tbf.exportError")}
                >
                  {t("tbf.export")}
                </ExportButton>
              )}
            </div>
          }
        >
          <Text type="secondary" className="tbf-home__hint">
            {t("tbf.methodNote", { from: result.firstYear, to: result.lastYear, min: result.minCountries })}
          </Text>
          <Table
            rowKey={(row) => `${row.c_code}-${row.ind_code}`}
            columns={columns}
            dataSource={shownResults}
            loading={loading}
            locale={{ emptyText: t("tbf.empty") }}
            pagination={{ pageSize: 20, showSizeChanger: false }}
            rowClassName={(row) => (shadedRows.has(row) ? "tbf-home__row-shaded" : "")}
            onChange={(_, __, sorter) => setSort(Array.isArray(sorter) ? sorter[0] : sorter)}
            // Fixed layout keeps headers in place when sorting.
            tableLayout="fixed"
            scroll={{ x: 1000 }}
          />
        </Card>
      )}
    </div>
  );
}
